import { FC, useEffect, useState } from "react";
import styles from "./BoostModal.module.css";
import { useModalStore } from "../../../../Store/ModalStore";
import { useBoostTaskStore } from "../../../../Store/BoostTaskStore";
import { useMiningStore } from "../../../../Store/miningStore";
import { useNotificationStore } from "../../../../Store/NotificationStore";
import { BoostTask } from "../../types/boost";
import { BoostProviderManager } from "../../Provider/Provider";
import { boostMining } from "../../../../Services/Requests";
import { MiningService } from "../../services/MiningService";
import AdWidgetModal from "../../../../Components/AdWidgetFromTads/TadsWidgetModal";
import { useDoubleClickStore } from "../../../../Store/DoubleClickStore";

const BoostModal: FC = () => {
  const [task, setTask] = useState<BoostTask | null>(null);
  const [loading, setLoading] = useState(false);
  const setMiningData = useMiningStore((state) => state.setMiningData);
  const { open, setOpen, setClosing, source } = useModalStore();
  const { currentTask, setCurrentTask } = useBoostTaskStore();
  const { miningData, setLastBoostTime } = useMiningStore();
  const { setNotification } = useNotificationStore();
  const setDoubleClick = useDoubleClickStore((state) => state.setDoubleClick);
  useEffect(() => {
    if (open) {
      console.log("BoostModal opened from:", source);
      if (currentTask) {
        setTask(currentTask);
        setLoading(false);
      } else {
        loadTask();
      }
    }

    return () => {
      setCurrentTask(null); // Очищаем стор при закрытии
    };
  }, [open, source]);

  const loadTask = async () => {
    setLoading(true);
    try {
      const providers = BoostProviderManager.getProviders();

      for (const provider of providers) {
        if (provider.getName() === "Tads") continue;

        const tasks = await provider.getAvailableTasks(
          miningData?.mining?.id || 0
        );

        if (tasks?.length) {
          const firstTask = tasks[0];
          setTask(firstTask);
          setCurrentTask(firstTask);
          break;
        }
      }
    } catch (error) {
      console.error("Ошибка загрузки заданий:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => setOpen(false), 300);
  };

  const handleTaskClick = async () => {
    if (!task) return;

    try {
      handleClose();
      const result = await task.onClick();

      if (result === "success" && miningData?.mining?.id) {
        const providerMap: Record<string, string> = {
          TappAds: "tappads",
          onclick: "onclick",
          Barza: "barzha",
          Adsgram: "adsgram",
          Tads: "tads",
          Flyer: "flyer",
          RichAds: "richads",
          DreamCoin: "dreamcoin",
        };

        const apiProvider = providerMap[task.provider];
        if (!apiProvider) {
          console.error(`Unknown provider: ${task.provider}`);
          setNotification({
            type: "Error",
            message: "Неизвестный провайдер рекламы",
          });
          return;
        }

        if (source === "miningCard") {
          try {
            await boostMining(
              miningData.mining.id,
              task.provider.toLowerCase(),
              setNotification,
              "claim"
            );
            const resp = await MiningService.claimMining(miningData.mining.id);
            console.log(resp);
            setMiningData(resp);
            setDoubleClick(false);
          } catch (error) {
            console.error("Ошибка сбора награды", error);
            setNotification({
              type: "Error",
              message: "Не удалось собрать награду",
            });
            setDoubleClick(false);
          }
        } else {
          try {
            console.log("Applying boost for task:", task.provider);
            const response = await boostMining(
              miningData.mining.id,
              apiProvider,
              setNotification
            );
            if (response?.boost_duration || response?.last_boost_time) {
              const updatedData = await MiningService.fetchMiningData();
              if (updatedData.last_boost_time) {
                setLastBoostTime(updatedData.last_boost_time);
                setNotification({
                  type: "Success",
                  message: "Майнинг ускорен на 30 минут! 🚀",
                });
              }
            }
          } catch (error) {
            console.error("Error applying boost:", error);
            setNotification({
              type: "Error",
              message: "Не удалось применить ускорение",
            });
          }
        }
      } else if (result === "user_cancelled") {
        console.log("Task cancelled by user");
      } else {
        setNotification({
          type: "Error",
          message: "Не удалось выполнить задание",
        });
      }
    } catch (err) {
      console.error("Error executing task:", err);
      setNotification({
        type: "Error",
        message: "Произошла ошибка при выполнении задания",
      });
    }
  };

  const getFirstLetter = (title: string) => {
    // Регулярное выражение для проверки эмодзи
    const emojiRegex =
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE00}-\u{FE0F}]|[\u{1FAB0}-\u{1FABF}]|[\u{1FAC0}-\u{1FAFF}]|[\u{1FAD0}-\u{1FAFF}]|[\u{1FAE0}-\u{1FAFF}]|[\u{1FAF0}-\u{1FAFF}]/u;

    // Проверяем, содержит ли заголовок эмодзи
    if (emojiRegex.test(title)) {
      // Если есть эмодзи, берем второй символ (пропускаем эмодзи)
      return title.slice(1, 2);
    } else {
      // Если нет эмодзи, берем первую букву
      return title.slice(0, 1);
    }
  };

  const placeholderImage = task ? (
    <div className={styles.taskIconPlaceholder}>
      {task.title ? getFirstLetter(task.title) : ""}
    </div>
  ) : (
    <div className={styles.taskIconPlaceholder}></div>
  );

  return (
    <div className={styles.boostModal}>
      {task?.provider === "Tads" ? (
        <AdWidgetModal
          isOpen={true}
          isClosing={false}
          onClose={handleClose}
          onReward={handleTaskClick}
          onAdsNotFound={() => {
            setNotification({
              type: "Warning",
              message: "Реклама не найдена, пробуем другой способ",
            });
            handleClose();
          }}
          checkOnly={false}
        />
      ) : (
        <>
          <p className={styles.rewardText}>
            {source === "miningCard"
              ? "Чтобы забрать награду,"
              : "Нажми на баннер,"}
            <br />{" "}
            {source === "miningCard"
              ? "нажми на баннер."
              : "чтобы ускорить майнинг на 30 минут."}
          </p>

          {loading ? (
            <div className={styles.loading}>Загрузка задания...</div>
          ) : task ? (
            <div className={styles.modalContent} onClick={handleTaskClick}>
              <div className={styles.modalContentContainer}>
                {task.icon &&
                task.icon !==
                  "https://i.ibb.co/1fL03jFC/photo-2025-06-20-13-59-47.jpg" ? (
                  <img
                    src={task.icon}
                    alt="Task Icon"
                    className={styles.taskIconModal}
                  />
                ) : (
                  placeholderImage
                )}
                <div className={styles.modalTitleContainer}>
                  <h3 className={styles.modalTitle}>{task.title}</h3>
                  <p className={styles.modalDescription}>{task.description}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.noTasks}>Нет доступных заданий</div>
          )}

          <p className={styles.rewardText_warning}>
            *Остерегайтесь мошенников.
            <br />
            Подписываться необязательно.
          </p>
        </>
      )}
    </div>
  );
};

export default BoostModal;
